import { useState } from "react";
import { ArrowRight } from "lucide-react";
import type { LibraryExam, LibrarySummary } from "../../../../packages/published-content/library";
import type { Timing } from "../../../../packages/question-presentation/timing";
import { sectionScopeLabel, type SectionScope } from "../section-scope";
import { useQueryState } from "../navigation";
import { PageHeading, RequestState } from "../PortalShell";
import { displayTitle, formatDueDateTime, statusLabel, useResource, type Assignment } from "../portal-data";
import { activeAssignmentForExam } from "./student-test-availability";
import { SearchTests, TestRow } from "./test-list";
import { testSectionAvailability } from "./test-catalog-status";

type AttemptSummary = {
  id: string;
  exam_id: string;
  exam_title: string;
  status: "in_progress" | "completed" | "abandoned";
  started_at: number;
  section_scope: SectionScope;
  timing: Timing;
  score: { percentage?: number } | null;
};

export function StudentTests({ onStart, onResume, onResults, busy, error }: {
  onStart: (exam: { id: string }, assignmentId?: string, options?: { timing: Timing; section: SectionScope }) => void;
  onResume: (id: string) => void;
  onResults: (id: string) => void;
  busy: boolean;
  error: string;
}) {
  const library = useResource<{ release: LibrarySummary }>("/api/library");
  const assignments = useResource<{ assignments: Assignment[]; nextCursor: string | null }>("/api/assignments?compact=true&agenda=true");
  const sessions = useResource<{ attempts: AttemptSummary[]; nextCursor: string | null }>("/api/attempts?status=all");
  const [search, setSearch] = useQueryState("search", "");
  const [requestedSection, setSection] = useQueryState<SectionScope>("section", "full");
  const [requestedTiming, setTiming] = useQueryState<Timing>("timing", "untimed");
  const [shown, setShown] = useState(12);
  const section: SectionScope = requestedSection === "math" || requestedSection === "rw" ? requestedSection : "full";
  const timing: Timing = requestedTiming === "timed" ? "timed" : "untimed";
  const rows = assignments.data?.assignments ?? [];
  const allExams = library.data?.release.exams ?? [];
  const normalizedSearch = search.trim().toLocaleLowerCase();
  const available = allExams
    .filter((exam) => {
      const sectionAvailability = section === "full" ? null : testSectionAvailability(exam, section);
      return (!normalizedSearch || `${exam.title} ${exam.sitting ?? ""}`.toLocaleLowerCase().includes(normalizedSearch))
        && (timing === "untimed" || exam.supportsTiming)
        && (section === "full" || sectionAvailability === null || sectionAvailability.available > 0);
    })
    .sort((a, b) => (b.sitting ?? "").localeCompare(a.sitting ?? "") || a.title.localeCompare(b.title));
  const requestError = error || library.error || assignments.error || sessions.error;
  const loading = library.loading || assignments.loading || sessions.loading;

  return (
    <>
      <PageHeading title="Tests" />
      <div className="portal-content student-tests">
        {sessions.data?.attempts[0] && <LastSession attempt={sessions.data.attempts[0]} busy={busy} onResume={onResume} onResults={onResults} />}

        <section className="test-catalog" aria-labelledby="test-catalog-heading">
          <div className="section-title test-catalog-title">
            <h2 id="test-catalog-heading">Choose a test</h2>
            <span>{available.length} {available.length === 1 ? "test" : "tests"}</span>
          </div>

          <div className="test-options">
            <fieldset>
              <legend>Section</legend>
              <div className="segmented-control">
                {(["full", "rw", "math"] as const).map((scope) => (
                  <button key={scope} type="button" aria-pressed={section === scope} onClick={() => { setSection(scope); setShown(12); }}>
                    {scope === "rw" ? "Reading and Writing" : sectionScopeLabel(scope)}
                  </button>
                ))}
              </div>
            </fieldset>
            <fieldset>
              <legend>Timing</legend>
              <div className="segmented-control">
                {(["untimed", "timed"] as const).map((value) => (
                  <button key={value} type="button" aria-pressed={timing === value} disabled={value === "timed" && !allExams.some((exam) => exam.supportsTiming)} onClick={() => { setTiming(value); setShown(12); }}>
                    {value === "timed" ? "Timed" : "Untimed"}
                  </button>
                ))}
              </div>
            </fieldset>
            {(allExams.length > 6 || search) && <SearchTests search={search} onSearch={(value) => { setSearch(value); setShown(12); }} placeholder="Search tests" />}
          </div>

          <RequestState loading={loading} error={requestError} retry={() => { library.reload(); assignments.reload(); sessions.reload(); }} />
          <div className="test-list">
            {available.slice(0, shown).map((exam) => {
              const assignment = section === "full" ? activeAssignmentForExam(exam.id, rows) : null;
              return <StudentExamCard
                key={exam.id}
                exam={exam}
                assignment={assignment}
                section={section}
                timing={timing}
                busy={busy}
                onStart={onStart}
                onResume={onResume}
              />;
            })}
          </div>
          {available.length > shown && <div className="catalog-more"><span>Showing {shown} of {available.length}</span><button className="secondary-button" onClick={() => setShown((value) => value + 12)}>Show more</button></div>}
          {library.data && !available.length && !loading && <p className="empty-message">No tests match these options.</p>}
        </section>
      </div>
    </>
  );
}

function StudentExamCard({ exam, assignment, section, timing, busy, onStart, onResume }: {
  exam: LibraryExam;
  assignment: Assignment | null;
  section: SectionScope;
  timing: Timing;
  busy: boolean;
  onStart: (exam: { id: string }, assignmentId?: string, options?: { timing: Timing; section: SectionScope }) => void;
  onResume: (id: string) => void;
}) {
  const assignmentDetail = assignment
    ? `${assignment.timing === "timed" ? "Timed assignment" : "Untimed assignment"}${assignment.dueAt ? `, due ${formatDueDateTime(assignment.dueAt)}` : ""}`
    : undefined;
  const actionLabel = assignment?.attemptId && assignment.attemptStatus === "in_progress" ? "Continue" : assignment ? "Start" : timing === "timed" ? "Start timed" : "Start";
  const action = assignment?.attemptId && assignment.attemptStatus === "in_progress"
    ? () => onResume(assignment.attemptId!)
    : assignment
      ? () => onStart({ id: exam.id }, assignment.id)
      : () => onStart({ id: exam.id }, undefined, { timing, section });

  return <TestRow
    exam={exam}
    admin={false}
    scope={section}
    actionLabel={actionLabel}
    detail={assignmentDetail}
    status={assignment ? { label: statusLabel(assignment.status), tone: assignment.status === "in_progress" ? "alert" : "accent" } : undefined}
    disabled={busy}
    onAction={action}
  />;
}

function LastSession({ attempt, busy, onResume, onResults }: {
  attempt: AttemptSummary;
  busy: boolean;
  onResume: (id: string) => void;
  onResults: (id: string) => void;
}) {
  const tone = attempt.status === "completed" ? "good" : attempt.status === "in_progress" ? "alert" : "neutral";
  return (
    <section className="last-session" aria-labelledby="last-session-heading">
      <div>
        <div className="last-session-heading">
          <h2 id="last-session-heading">Last session</h2>
          <span className={`status-badge ${tone}`}>{statusLabel(attempt.status)}</span>
        </div>
        <h3>{displayTitle(attempt.exam_title)}</h3>
        <p className="last-session-meta">
          <span>{sectionScopeLabel(attempt.section_scope ?? "full")}</span>
          <span>{attempt.timing === "timed" ? "Timed" : "Untimed"}</span>
          <time dateTime={new Date(attempt.started_at * 1000).toISOString()}>{new Date(attempt.started_at * 1000).toLocaleDateString(undefined, { dateStyle: "medium" })}</time>
        </p>
      </div>
      <div className="last-session-action">
        {attempt.status === "completed" && attempt.score?.percentage != null && <strong>{attempt.score.percentage}% <small>accuracy</small></strong>}
        {attempt.status === "in_progress" && <button className="primary-button" disabled={busy} onClick={() => onResume(attempt.id)}>Continue <ArrowRight size={14} /></button>}
        {attempt.status === "completed" && <button className="secondary-button" onClick={() => onResults(attempt.id)}>Review <ArrowRight size={14} /></button>}
      </div>
    </section>
  );
}
